/**
 * @fileoverview Gmail-style draft autosave for the mail composers (MOR-783).
 *
 * Debounces ~1.5s after the last change, creates the `mailDrafts` row
 * (source "user") on first real content, patches it thereafter, and flushes
 * on unmount so navigate-away between ticks still saves. Failures are
 * silent — the next tick retries; autosave must never block typing.
 *
 * Sending hands the row to the backend: `flush()` drains any in-flight save
 * and writes the latest payload so `composeUserEmail` can stage THAT row as
 * the send record, and `release()` then stops autosave without deleting it
 * (a failed send reappears in Drafts as the same draft). `discard()` remains
 * the explicit delete.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { ComposerAttachment } from "./types";

const AUTOSAVE_DEBOUNCE_MS = 1500;

export interface DraftAutosavePayload {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyText: string;
  bodyHtml?: string;
  attachments: ComposerAttachment[];
}

/**
 * A row is only worth creating once the user has written something real.
 *
 * Addressees alone do not count. A composer opened against a known party —
 * a reply, or a new message from a contact record — arrives with `to`
 * already filled, and treating that as content would file an empty
 * "(no subject)" draft the moment the composer opened, on every surface
 * that lists drafts. There is nothing in such a row to lose.
 */
export function hasDraftContent(p: DraftAutosavePayload): boolean {
  return (
    p.subject.trim().length > 0 ||
    p.bodyText.trim().length > 0 ||
    p.attachments.length > 0
  );
}

export function useDraftAutosave({
  jobId,
  enabled,
  initialDraftId,
  threadId,
  inReplyToInkboxMessageId,
  payload,
}: {
  jobId: Id<"jobs">;
  enabled: boolean;
  initialDraftId?: Id<"mailDrafts"> | null;
  threadId?: Id<"mailThreads">;
  inReplyToInkboxMessageId?: string;
  payload: DraftAutosavePayload;
}) {
  const saveUserDraft = useMutation(api.mail.userDrafts.saveUserDraft);
  const deleteUserDraft = useMutation(api.mail.userDrafts.deleteUserDraft);

  const [draftId, setDraftId] = useState<Id<"mailDrafts"> | null>(
    initialDraftId ?? null,
  );
  const draftIdRef = useRef<Id<"mailDrafts"> | null>(initialDraftId ?? null);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  // The save currently on the wire (null when idle); a change that lands
  // mid-flight sets `pending` so one follow-up save runs when it settles.
  const inFlightRef = useRef<Promise<void> | null>(null);
  const pendingRef = useRef(false);
  const stoppedRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // `initialDraftId` can arrive AFTER mount (e.g. getUserDraftForThread
  // resolving) — adopt it so we patch the existing row instead of creating
  // a duplicate. Never overwrite an id we already created.
  useEffect(() => {
    if (initialDraftId && !draftIdRef.current) {
      draftIdRef.current = initialDraftId;
      setDraftId(initialDraftId);
    }
  }, [initialDraftId]);

  const performSave = async (): Promise<void> => {
    const current = payloadRef.current;
    if (!draftIdRef.current && !hasDraftContent(current)) return;
    try {
      const id = await saveUserDraft({
        draftId: draftIdRef.current ?? undefined,
        jobId,
        threadId,
        inReplyToInkboxMessageId,
        to: current.to,
        cc: current.cc,
        bcc: current.bcc,
        subject: current.subject,
        bodyText: current.bodyText,
        bodyHtml: current.bodyHtml,
        attachments: current.attachments.map((a) => ({
          filename: a.filename,
          contentType: a.contentType,
          storageId: a.storageId,
          documentId: a.documentId,
        })),
      });
      if (!stoppedRef.current) {
        draftIdRef.current = id;
        setDraftId(id);
      }
    } catch {
      // Silent: autosave retries on the next debounce tick.
    }
  };

  /**
   * Saves the current payload (coalescing with any save already in flight).
   * `force` bypasses `enabled` — the send path disables autosave while the
   * request is out but still needs one final write first.
   */
  const save = (opts: { force?: boolean } = {}): Promise<void> => {
    if (stoppedRef.current) return Promise.resolve();
    if (!opts.force && !enabledRef.current) return Promise.resolve();
    if (inFlightRef.current) {
      pendingRef.current = true;
      return inFlightRef.current;
    }
    const run = performSave().finally(() => {
      inFlightRef.current = null;
      if (pendingRef.current) {
        pendingRef.current = false;
        void save(opts);
      }
    });
    inFlightRef.current = run;
    return run;
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  // Debounced save on every payload change.
  const serialized = JSON.stringify(payload);
  useEffect(() => {
    if (!enabled || stoppedRef.current) return;
    const timer = setTimeout(
      () => void saveRef.current(),
      AUTOSAVE_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [serialized, enabled]);

  // Flush on unmount (covers navigate-away between ticks).
  useEffect(() => {
    return () => {
      void saveRef.current();
    };
  }, []);

  /**
   * Waits for any in-flight save, writes the latest payload, and returns the
   * draft id (null when nothing was ever worth saving). Called right before
   * send so the send can adopt this exact row — and so no autosave can land
   * AFTER the send and leave an orphaned duplicate in Drafts.
   */
  const flush = async (): Promise<Id<"mailDrafts"> | null> => {
    if (stoppedRef.current) return draftIdRef.current;
    while (inFlightRef.current) await inFlightRef.current;
    await save({ force: true });
    return draftIdRef.current;
  };

  /** Stops all future saves WITHOUT deleting the row (post-send hand-off). */
  const release = () => {
    stoppedRef.current = true;
    draftIdRef.current = null;
    setDraftId(null);
  };

  /** Deletes the draft row and stops all future saves (Discard). */
  const discard = async () => {
    const id = draftIdRef.current;
    release();
    if (id) {
      try {
        await deleteUserDraft({ draftId: id });
      } catch {
        // Best-effort; an orphaned draft is discoverable in the folder.
      }
    }
  };

  return { draftId, flush, release, discard };
}
